/**
 * Service for managing custom follow-up statements
 * Uses Office.context.roamingSettings for cross-device persistence with localStorage fallback
 */

// Default follow-up statements
const DEFAULT_FOLLOWUP_STATEMENTS = {
  'default': {
    id: 'default',
    name: 'Default',
    content: `Thank you for your time and assistance with this matter. If you have any questions, please feel free to contact me.`,
    isCustom: false,
    isEditable: false,
    createdDate: null
  }
};

// Storage keys - Base keys that will be prefixed with context
const STORAGE_KEYS = {
  CUSTOM_STATEMENTS: 'customFollowUpStatements',
  SELECTED_STATEMENT: 'selectedFollowUpStatement'
};

/**
 * Get context-aware storage key
 * @param {string} baseKey - Base storage key
 * @param {string} context - Context ('client' or 'property')
 * @returns {string} Context-aware storage key
 */
function getStorageKey(baseKey, context = 'client') {
  return `${baseKey}_${context}`;
}

/**
 * Initialize the follow-up statement service
 * @param {string} context - Context ('client' or 'property')
 */
export async function initializeFollowUpStatementService(context = 'client') {
  try {
    console.log(`🔧 Initializing Follow-Up Statement Service for ${context}...`);
    
    // Ensure we have a selected statement
    const selectedId = await getSelectedStatementId(context);
    if (!selectedId) {
      await setSelectedStatementId('default', context);
    }
    
    console.log(`✅ Follow-Up Statement Service initialized for ${context}`);
    return true;
  } catch (error) {
    console.error(`❌ Failed to initialize Follow-Up Statement Service for ${context}:`, error);
    return false;
  }
}

/**
 * Get all follow-up statements (default + custom)
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<Array>} Array of follow-up statement objects
 */
export async function getAllFollowUpStatements(context = 'client') {
  try {
    const customStatements = await getCustomStatements(context);
    const allStatements = Object.values(DEFAULT_FOLLOWUP_STATEMENTS);
    
    // Add custom statements at the top
    const customStatementsArray = Object.values(customStatements);
    customStatementsArray.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    
    return [...customStatementsArray, ...allStatements];
  } catch (error) {
    console.error('Error getting all follow-up statements:', error);
    return Object.values(DEFAULT_FOLLOWUP_STATEMENTS);
  }
}

/**
 * Get custom statements from storage
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<Object>} Object with custom statements
 */
async function getCustomStatements(context = 'client') {
  try {
    const storageKey = getStorageKey(STORAGE_KEYS.CUSTOM_STATEMENTS, context);
    
    // Try Office.js roaming settings first
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      const roamingData = Office.context.roamingSettings.get(storageKey);
      if (roamingData) {
        console.log(`📱 Loaded custom statements from roaming settings (${context})`);
        return JSON.parse(roamingData);
      }
    }
    
    // Fallback to localStorage
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      console.log(`💾 Loaded custom statements from localStorage (${context})`);
      return JSON.parse(localData);
    }
    
    return {};
  } catch (error) {
    console.error('Error loading custom statements:', error);
    return {};
  }
}

/**
 * Save custom statements to storage
 * @param {Object} statements - Custom statements object
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<boolean>} Success status
 */
async function saveCustomStatements(statements, context = 'client') {
  try {
    const storageKey = getStorageKey(STORAGE_KEYS.CUSTOM_STATEMENTS, context);
    const dataString = JSON.stringify(statements);
    
    // Save to Office.js roaming settings (cross-device)
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(storageKey, dataString);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log(`✅ Custom statements saved to roaming settings (${context})`);
        } else {
          console.warn(`⚠️ Failed to save to roaming settings, using localStorage only (${context})`);
        }
      });
    }
    
    // Also save to localStorage as fallback
    localStorage.setItem(storageKey, dataString);
    console.log(`💾 Custom statements saved to localStorage (${context})`);
    
    return true;
  } catch (error) {
    console.error('Error saving custom statements:', error);
    return false;
  }
}

/**
 * Create a new custom follow-up statement
 * @param {string} name - Name/title for the statement
 * @param {string} content - Content of the statement
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<Object>} Created statement object or null if failed
 */
export async function createCustomFollowUpStatement(name, content, context = 'client') {
  try {
    const customStatements = await getCustomStatements(context);
    
    // Generate unique ID
    const id = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Create new statement
    const newStatement = {
      id,
      name: name.trim(),
      content: content, // Don't trim HTML content as it can break formatting
      isCustom: true,
      isEditable: true,
      createdDate: new Date().toISOString()
    };
    
    // Add to custom statements
    customStatements[id] = newStatement;
    
    // Save to storage
    await saveCustomStatements(customStatements, context);
    
    console.log('✅ Created new custom follow-up statement:', newStatement.name);
    return newStatement;
  } catch (error) {
    console.error('❌ Failed to create custom follow-up statement:', error);
    throw error;
  }
}

/**
 * Update an existing custom follow-up statement
 * @param {string} id - Statement ID
 * @param {string} name - Updated name
 * @param {string} content - Updated content
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<Object>} Updated statement object or null if failed
 */
export async function updateCustomFollowUpStatement(id, name, content, context = 'client') {
  try {
    const customStatements = await getCustomStatements(context);
    
    if (!customStatements[id]) {
      throw new Error('Custom statement not found');
    }
    
    if (!customStatements[id].isEditable) {
      throw new Error('This statement is not editable');
    }
    
    // Update statement
    customStatements[id] = {
      ...customStatements[id],
      name: name.trim(),
      content: content, // Don't trim HTML content as it can break formatting
      modifiedDate: new Date().toISOString()
    };
    
    // Save to storage
    await saveCustomStatements(customStatements, context);
    
    console.log('✅ Updated custom follow-up statement:', customStatements[id].name);
    return customStatements[id];
  } catch (error) {
    console.error('❌ Failed to update custom follow-up statement:', error);
    throw error;
  }
}

/**
 * Delete a custom follow-up statement
 * @param {string} id - Statement ID
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<boolean>} Success status
 */
export async function deleteCustomFollowUpStatement(id, context = 'client') {
  try {
    const customStatements = await getCustomStatements(context);
    
    if (!customStatements[id]) {
      throw new Error('Custom statement not found');
    }
    
    if (!customStatements[id].isEditable) {
      throw new Error('This statement cannot be deleted');
    }
    
    // Check if this is the currently selected statement
    const selectedId = await getSelectedStatementId(context);
    if (selectedId === id) {
      // Reset to default if deleting currently selected
      await setSelectedStatementId('default', context);
    }
    
    // Delete statement
    delete customStatements[id];
    
    // Save to storage
    await saveCustomStatements(customStatements, context);
    
    console.log('✅ Deleted custom follow-up statement:', id);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete custom follow-up statement:', error);
    throw error;
  }
}

/**
 * Get selected follow-up statement ID
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<string>} Selected statement ID
 */
export async function getSelectedStatementId(context = 'client') {
  try {
    const storageKey = getStorageKey(STORAGE_KEYS.SELECTED_STATEMENT, context);
    
    // Try Office.js roaming settings first
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      const roamingSelection = Office.context.roamingSettings.get(storageKey);
      if (roamingSelection) {
        return roamingSelection;
      }
    }
    
    // Fallback to localStorage
    const localSelection = localStorage.getItem(storageKey);
    return localSelection || 'default';
  } catch (error) {
    console.error('Error getting selected statement ID:', error);
    return 'default';
  }
}

/**
 * Set selected follow-up statement ID
 * @param {string} id - Statement ID to select
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<boolean>} Success status
 */
export async function setSelectedStatementId(id, context = 'client') {
  try {
    const storageKey = getStorageKey(STORAGE_KEYS.SELECTED_STATEMENT, context);
    
    // Save to Office.js roaming settings
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(storageKey, id);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log(`✅ Selected statement ID saved to roaming settings (${context})`);
        }
      });
    }
    
    // Also save to localStorage
    localStorage.setItem(storageKey, id);
    console.log(`💾 Selected statement ID saved (${context}):`, id);
    
    return true;
  } catch (error) {
    console.error('Error setting selected statement ID:', error);
    return false;
  }
}

/**
 * Get the currently selected follow-up statement
 * @param {string} context - Context ('client' or 'property')
 * @returns {Promise<Object>} Selected statement object
 */
export async function getSelectedFollowUpStatement(context = 'client') {
  try {
    const selectedId = await getSelectedStatementId(context);
    const allStatements = await getAllFollowUpStatements(context);
    
    const selectedStatement = allStatements.find(s => s.id === selectedId);
    return selectedStatement || allStatements.find(s => s.id === 'default');
  } catch (error) {
    console.error('Error getting selected follow-up statement:', error);
    return DEFAULT_FOLLOWUP_STATEMENTS['default'];
  }
}

/**
 * Get word count for text
 * @param {string} text - Text to count
 * @returns {number} Word count
 */
export function getWordCount(text) {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

export default {
  initializeFollowUpStatementService,
  getAllFollowUpStatements,
  createCustomFollowUpStatement,
  updateCustomFollowUpStatement,
  deleteCustomFollowUpStatement,
  getSelectedStatementId,
  setSelectedStatementId,
  getSelectedFollowUpStatement,
  getWordCount
};
